import React, { useState } from 'react';
import { Button } from '@/shared/components/ui/button/Button';

interface MfaRecoveryCodesDisplayProps {
    codes: string[];
    onConfirm: () => void;
}

export const MfaRecoveryCodesDisplay: React.FC<MfaRecoveryCodesDisplayProps> = ({
    codes,
    onConfirm,
}) => {
    const [saved, setSaved] = useState(false);
    const [copied, setCopied] = useState(false);

    const handleCopyAll = async () => {
        await navigator.clipboard.writeText(codes.join('\n'));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleDownload = () => {
        const blob = new Blob([codes.join('\n')], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'recovery-codes.txt';
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="flex flex-col flex-1">
            <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
                <div className="mb-5 sm:mb-8 space-y-3">
                    <h1 className="mb-2 font-semibold text-mp-foreground text-title-sm sm:text-title-md">
                        Save your recovery codes
                    </h1>
                    <p className="text-sm text-mp-muted">
                        These codes can be used to access your account if you lose your
                        authentication device. Each code can only be used once.
                    </p>
                    <p className="text-sm font-semibold text-mp-error">
                        Save these now — they will never be shown again.
                    </p>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-4 p-4 bg-mp-background rounded-lg border border-mp-border">
                    {codes.map((code) => (
                        <span
                            key={code}
                            className="font-mono text-sm text-mp-foreground py-1 px-2 bg-mp-surface rounded border border-mp-border text-center"
                        >
                            {code}
                        </span>
                    ))}
                </div>

                <div className="flex gap-2 mb-6">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleCopyAll}
                        className="flex-1"
                    >
                        {copied ? 'Copied!' : 'Copy all'}
                    </Button>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleDownload}
                        className="flex-1"
                    >
                        Download .txt
                    </Button>
                </div>

                <label className="flex items-start gap-3 mb-6 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={saved}
                        onChange={(e) => setSaved(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-mp-input text-mp-primary focus:ring-mp-primary"
                    />
                    <span className="text-sm text-mp-foreground">
                        I have saved my recovery codes in a safe place
                    </span>
                </label>

                <Button onClick={onConfirm} disabled={!saved} className="w-full" size="sm">
                    Continue
                </Button>
            </div>
        </div>
    );
};
